import { call, channel, spawn, take, takeEvery, Channel } from "../../lib";
import {
  flushQuestionnaireDrafts,
  QuestionnaireDraftScopedRequest,
  QuestionnaireDraftTransport,
  QuestionnaireDraftUpsertRequest,
  configureQuestionnaireDraftTransport,
} from "../../lib/questionnaire_draft/coordinator";
import { QuestionnaireDraftKey, QuestionnaireDraftSnapshot } from "../../model/questionnaire_draft";
import {
  sendQuestionnaireDraftUpsert,
  sendFinalQuestionnaireDraftUpsert,
} from "./questionnaire_draft_upsert";
import { sendQuestionnaireDraftFetch } from "./questionnaire_draft_fetch";
import { sendQuestionnaireDraftDelete } from "./questionnaire_draft_delete";
import { fetchMethodForDraftRestore } from "./method_fetch";
import { SessionScope } from "../../lib/auth/session_scope";
import { SessionActionType } from "../state";

type TransportRequest =
  | { type: "upsert"; request: QuestionnaireDraftUpsertRequest; resolve: (outcome: any) => void }
  | { type: "final"; request: { key: QuestionnaireDraftKey; draft: QuestionnaireDraftSnapshot } }
  | { type: "fetch"; request: QuestionnaireDraftScopedRequest; resolve: (outcome: any) => void }
  | { type: "delete"; request: QuestionnaireDraftScopedRequest; resolve: (outcome: any) => void }
  | {
      type: "saved_tasks";
      request: { scope: SessionScope; methodId: string };
      resolve: (outcome: any) => void;
    };

// Runs one worker and answers its caller whatever happens. A worker that throws -- a reducer guard on a
// half-shaped reply, say -- answers `retry` rather than leaving its caller waiting, and it is spawned,
// not forked, so the error cannot end the transport and every later draft request with it. Only the
// error's name is logged: an axios or Joi error can quote the draft it carried.
function* answer<T>(
  worker: () => Generator<any, T, any>,
  resolve: (outcome: T | { kind: "retry" }) => void,
) {
  try {
    resolve(yield* worker());
  } catch (error) {
    console.error(
      "saga*questionnaireDraftTransport request failed:",
      error instanceof Error ? error.name : typeof error,
    );
    resolve({ kind: "retry" });
  }
}

// Connects the draft coordinator to this store's saga workers. Requests come in through a channel
// rather than as actions, so a draft never passes through `dispatch` and so never reaches the logger,
// DevTools, the loading indicator or request state.
export function* questionnaireDraftTransportSaga() {
  const requests: Channel<TransportRequest> = yield call(channel);
  const transport: QuestionnaireDraftTransport = {
    upsert: (request) =>
      new Promise((resolve) => requests.put({ type: "upsert", request, resolve })),
    finalUpsert: (request) => requests.put({ type: "final", request }),
    fetch: (request) => new Promise((resolve) => requests.put({ type: "fetch", request, resolve })),
    delete: (request) =>
      new Promise((resolve) => requests.put({ type: "delete", request, resolve })),
    fetchSavedTasks: (request) =>
      new Promise((resolve) => requests.put({ type: "saved_tasks", request, resolve })),
  };
  configureQuestionnaireDraftTransport(transport);

  for (;;) {
    const next: TransportRequest = yield take(requests);
    switch (next.type) {
      case "upsert":
        yield spawn(answer, () => sendQuestionnaireDraftUpsert(next.request), next.resolve);
        break;
      case "final":
        yield spawn(
          answer,
          () => sendFinalQuestionnaireDraftUpsert(next.request),
          () => undefined,
        );
        break;
      case "fetch":
        yield spawn(answer, () => sendQuestionnaireDraftFetch(next.request), next.resolve);
        break;
      case "delete":
        yield spawn(answer, () => sendQuestionnaireDraftDelete(next.request), next.resolve);
        break;
      case "saved_tasks":
        yield spawn(answer, () => fetchMethodForDraftRestore(next.request), next.resolve);
        break;
    }
  }
}

// A session warning has just appeared: every draft is saved now, without waiting for its interval
export function* questionnaireDraftWarningFlushSaga() {
  yield takeEvery(SessionActionType.SESSION_WARNING_ENTERED, flushQuestionnaireDrafts);
}
